import React, { useState, useEffect } from "react";
import { InfoCircle, ExclamationTriangle } from "react-bootstrap-icons";

const deriveUnit = (minutes) => {
  if (minutes === 0) return "minute";
  if (minutes % 1440 === 0) return "day";
  if (minutes % 60 === 0) return "hour";
  return "minute";
};

const convertToMinutes = (value, unit) => {
  const v = Number(value) || 0;
  if (unit === "day") return v * 1440;
  if (unit === "hour") return v * 60;
  return v;
};

const convertFromMinutes = (minutes, unit) => {
  if (unit === "day") return Math.floor(minutes / 1440);
  if (unit === "hour") return Math.floor(minutes / 60);
  return minutes;
};

const BookingRulesEditor = ({
  bufferTime,
  minNotice,
  bookingWindow,
  onChange,
  errors,
}) => {
  const [noticeUnit, setNoticeUnit] = useState(() => deriveUnit(minNotice));
  const [noticeValue, setNoticeValue] = useState(() =>
    convertFromMinutes(minNotice, deriveUnit(minNotice))
  );

  useEffect(() => {
    const unit = deriveUnit(minNotice);
    setNoticeUnit(unit);
    setNoticeValue(convertFromMinutes(minNotice, unit));
  }, [minNotice]);

  const handleBufferChange = (e) => {
    const val = e.target.value === "" ? "" : Number(e.target.value);
    onChange({ bufferTime: val, minNotice, bookingWindow });
  };

  const handleBufferBlur = (e) => {
    const val = Number(e.target.value);
    if (!isNaN(val) && val > 0) {
      const rounded = Math.round(val / 5) * 5;
      onChange({ bufferTime: rounded, minNotice, bookingWindow });
    }
  };

  const handleNoticeValueChange = (e) => {
    const v = e.target.value === "" ? "" : Number(e.target.value);
    setNoticeValue(v);
    const mins = convertToMinutes(v, noticeUnit);
    onChange({ bufferTime, minNotice: mins, bookingWindow });
  };

  const handleNoticeUnitChange = (e) => {
    const newUnit = e.target.value;
    setNoticeUnit(newUnit);
    const mins = convertToMinutes(noticeValue, newUnit);
    onChange({ bufferTime, minNotice: mins, bookingWindow });
  };

  const handleWindowChange = (e) => {
    const val = e.target.value === "" ? "" : Number(e.target.value);
    onChange({ bufferTime, minNotice, bookingWindow: val });
  };

  const validationErrors = errors || {};
  const bufferVal = Number(bufferTime);
  const windowVal = Number(bookingWindow);

  const groupClass = (warning, invalid) => {
    let cls = "cbs-rules-input-group";
    if (invalid) cls += " cbs-rules-invalid";
    else if (warning) cls += " cbs-rules-warning";
    return cls;
  };

  return (
    <div className="booking-settings-section">
      <h4 className="booking-settings-section-title">Booking Rules</h4>
      <div className="cbs-rules-grid">
        {/* Buffer Time */}
        <div className="cbs-rules-item">
          <label className="cbs-rules-label">Buffer Time</label>
          <div
            className={groupClass(
              bufferVal === 0,
              validationErrors.bufferTime
            )}
          >
            <input
              type="number"
              min={0}
              step={5}
              value={bufferTime}
              onChange={handleBufferChange}
              onBlur={handleBufferBlur}
              className="cbs-rules-input"
            />
            <span className="cbs-rules-suffix">minute</span>
          </div>
          {validationErrors.bufferTime && (
            <div className="cbs-rules-error">{validationErrors.bufferTime}</div>
          )}
          {bufferVal === 0 && !validationErrors.bufferTime && (
            <div className="cbs-rules-caution">
              <ExclamationTriangle size={12} />
              Caution: risks back-to-back bookings
            </div>
          )}
          {bufferVal !== 0 && !validationErrors.bufferTime && (
            <div className="cbs-rules-description">
              <InfoCircle size={13} className="cbs-rules-info-icon" />
              <span>Add a gap after every job</span>
            </div>
          )}
        </div>

        {/* Minimum Notice */}
        <div className="cbs-rules-item">
          <label className="cbs-rules-label">Minimum Notice</label>
          <div
            className={groupClass(
              Number(minNotice) === 0,
              validationErrors.minNotice
            )}
          >
            <input
              type="number"
              min={0}
              value={noticeValue}
              onChange={handleNoticeValueChange}
              className="cbs-rules-input"
            />
            <select
              value={noticeUnit}
              onChange={handleNoticeUnitChange}
              className="cbs-rules-unit-select"
            >
              <option value="minute">minute</option>
              <option value="hour">hour</option>
              <option value="day">day</option>
            </select>
          </div>
          {validationErrors.minNotice && (
            <div className="cbs-rules-error">{validationErrors.minNotice}</div>
          )}
          {Number(minNotice) === 0 && !validationErrors.minNotice && (
            <div className="cbs-rules-caution">
              <ExclamationTriangle size={12} />
              Caution: allows immediate bookings
            </div>
          )}
          {Number(minNotice) !== 0 && !validationErrors.minNotice && (
            <div className="cbs-rules-description">
              <InfoCircle size={13} className="cbs-rules-info-icon" />
              <span>Limit last-minute bookings</span>
            </div>
          )}
        </div>

        {/* Booking Window */}
        <div className="cbs-rules-item">
          <label className="cbs-rules-label">Booking Window</label>
          <div
            className={groupClass(
              windowVal === 0,
              validationErrors.bookingWindow
            )}
          >
            <input
              type="number"
              min={0}
              value={bookingWindow}
              onChange={handleWindowChange}
              className="cbs-rules-input"
            />
            <span className="cbs-rules-suffix">day</span>
          </div>
          {validationErrors.bookingWindow && (
            <div className="cbs-rules-error">
              {validationErrors.bookingWindow}
            </div>
          )}
          {windowVal === 0 && !validationErrors.bookingWindow && (
            <div className="cbs-rules-caution">
              <ExclamationTriangle size={12} />
              Caution: hides all future availability
            </div>
          )}
          {windowVal !== 0 && !validationErrors.bookingWindow && (
            <div className="cbs-rules-description">
              <InfoCircle size={13} className="cbs-rules-info-icon" />
              <span>How far in advance clients can book</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingRulesEditor;
